import React, { FC, useCallback, useEffect, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import Button from '../../../components/button/index';
import { CloseModalButton } from '../../../components/close-modal-button/index';
import { FormInput } from '../../../components/input/index';
import { Sheet, SheetContent } from '../../../components/sheet/index';
import { Tag, TagVariant } from '../../../components/tag/index';
import { useCompany } from '../../../hooks/company/use-company';
import { CompanyHooks, useFormat, useReactForm } from '../../../hooks/index';
import { CompanyFreqFilter } from '../../../types/company.types';
import {
  SubscriptionDuration,
  SubscriptionStatus,
  subscriptionStatusTitle,
  SubscriptionType,
} from '../../../types/plan.types';
import { companyTypeTranslation } from '../view-company-modal/view-company-modal.types';
import { FormSchema, formSchema } from './edit-subscription-modal.validation';
import { FrequencyCombobox } from './frequency-combobox';
import { SubscriptionTypeCombobox } from './subscription-type-combobox';

export type EditSubscriptionModalProps = {
  className?: string;
  companyId: string;
  handleClose: () => void;
  invalidateQuery: () => void;
  isOpenModal: boolean;
};

export const EditSubscriptionModal: FC<EditSubscriptionModalProps> = ({
  isOpenModal,
  handleClose,
  invalidateQuery,
  companyId,
  className,
}) => {
  const { company } = useCompany(companyId);
  const { changeSubscription, isPending } = CompanyHooks.useChangeSubscription();
  const { patchEnvelopeLimit, isPending: isEnvelopeLimitPending } =
    CompanyHooks.usePatchEnvelopeLimit();

  const {
    control,
    reset,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { isDirty },
  } = useReactForm({
    schema: formSchema,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  const { planType, frequency } = useWatch<FormSchema>({ control });
  const { data: subscription, isLoading } = CompanyHooks.useSubscriptionData({
    companyId,
    type: planType || SubscriptionType.ENTERPRISE,
    frequency: frequency || SubscriptionDuration.MONTHLY,
  });

  const { format } = useFormat();

  useEffect(() => {
    if (!company) return;

    reset({
      planType: company?.planType || SubscriptionType.ENTERPRISE,
      amount: (company?.latestSubscriptionData?.plan?.amount || 0) / 100,
      stakeholderLimit: company?.stakeholdersLimit ?? 0,
      frequency:
        company?.planFrequency === CompanyFreqFilter.MONTH
          ? SubscriptionDuration.MONTHLY
          : SubscriptionDuration.ANNUALY,
      currency: subscription?.currency?.toUpperCase(),
      docusignEnvelopeLimit: company.docusignEnvelope?.limit,
    });
  }, [company, reset]);

  const handleCloseModal = useCallback(() => {
    handleClose();
    invalidateQuery();
    reset();
  }, [handleClose, invalidateQuery, reset]);

  const submitHandler = useCallback(
    (data: FormSchema) => {
      const { amount, stakeholderLimit, frequency, planType, docusignEnvelopeLimit } = data;
      const envelopeLimitChanged =
        docusignEnvelopeLimit != null && docusignEnvelopeLimit !== company?.docusignEnvelope?.limit;

      changeSubscription(
        {
          companyId,
          data: {
            amount: amount * 100,
            frequency: frequency,
            stakeholderLimit,
            type: planType,
          },
        },
        {
          onSuccess: () => {
            if (envelopeLimitChanged) {
              patchEnvelopeLimit(
                {
                  companyId,
                  data: { docusignEnvelopeLimit: docusignEnvelopeLimit! },
                },
                {
                  onSuccess: () => {
                    toast.success('Subscription and envelope limit updated');
                    invalidateQuery();
                    handleCloseModal();
                  },
                  onError: () => {
                    toast.success('Subscription updated');
                    invalidateQuery();
                    handleCloseModal();
                  },
                },
              );
            } else {
              toast.success('Subscription successfully updated');
              invalidateQuery();
              handleCloseModal();
            }
          },
        },
      );
    },
    [
      companyId,
      company?.docusignEnvelope?.limit,
      changeSubscription,
      handleCloseModal,
      invalidateQuery,
      patchEnvelopeLimit,
    ],
  );

  const handleUpdate = () => {
    handleSubmit(submitHandler, () => {
      toast.error('Please enter a valid amount and stakeholder limit');
    })();
  };

  useEffect(() => {
    if (!subscription || isDirtyRef.current) return;

    setValue('currency', subscription.currency.toUpperCase());

    if (
      company?.planType === SubscriptionType.ENTERPRISE &&
      planType === SubscriptionType.ENTERPRISE
    ) {
      setValue('amount', (company?.latestSubscriptionData?.plan?.amount || 0) / 100);
      setValue('stakeholderLimit', company?.stakeholdersLimit || 0);
    } else {
      setValue('amount', subscription.amount / 100);
      setValue('stakeholderLimit', subscription.stakeholderLimit);
      clearErrors();
    }
  }, [
    subscription,
    setValue,
    planType,
    company?.planType,
    company?.latestSubscriptionData?.plan?.amount,
    company?.stakeholdersLimit,
    clearErrors,
  ]);

  useEffect(() => {
    if (company?.docusignEnvelope?.hasOverride) {
      setValue('docusignEnvelopeLimit', company.docusignEnvelope.limit);
    }
  }, [company?.docusignEnvelope, setValue]);

  return (
    <Sheet open={isOpenModal}>
      <SheetContent
        className={twMerge(
          'w-full max-w-[485px] border-transparent bg-transparent p-2 shadow-none',
          className,
        )}
        onInteractOutside={handleCloseModal}
        side="RIGHT"
      >
        <div className="flex h-full w-full flex-col overflow-hidden rounded-lg border-[1px] border-gray-300 bg-gray-100 pb-4">
          <div className="flex h-fit w-full items-center justify-between bg-white px-6 py-3">
            <span className="truncate text-xl font-semibold text-gray-700">Edit Subscription</span>
            <CloseModalButton onClose={handleCloseModal} />
          </div>
          <div className="flex h-full flex-col gap-6 overflow-y-auto rounded-b-xl border-b border-gray-300 bg-white px-6 py-2">
            <div className="flex w-full justify-between gap-2">
              <div className="flex w-full flex-col gap-1 rounded-t-[4px] bg-gray-50 px-3 py-2">
                <span className="text-xs font-[450] text-gray-500">Creation Date</span>
                <span className="text-sm font-[450] text-gray-700">
                  {company?.createdAt && format(company?.createdAt, 'dd/MM/yyyy')}
                </span>
              </div>
              <div className="flex w-full flex-col gap-1 rounded-t-[4px] bg-gray-50 px-3 py-2">
                <span className="text-xs font-[450] text-gray-500">Company Type</span>
                <span className="text-sm font-[450] text-gray-700">
                  {company?.type && companyTypeTranslation[company.type]}
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-4">
              <div className="flex justify-between gap-1">
                <span className="py-[6px] text-m font-semibold text-gray-700">Subscription</span>
                <div className="flex items-center gap-3">
                  {company?.isEnterpriseRequested ? (
                    <Tag title="Awaiting for approval" variant={TagVariant.YELLOW} />
                  ) : company?.isActive && !company?.toCancelAt ? (
                    <Tag title="Active" variant={TagVariant.GREEN} />
                  ) : company?.toCancelAt && new Date(company?.toCancelAt) > new Date() ? (
                    <Tag
                      title={`Active until ${format(company?.toCancelAt, 'dd/MM/yyyy')}`}
                      variant={TagVariant.YELLOW}
                    />
                  ) : (
                    <Tag title="Inactive" variant={TagVariant.GRAY} />
                  )}

                  {company?.subscriptionStatus === SubscriptionStatus.ACTIVE ? (
                    <Tag title="Active" variant={TagVariant.GREEN} />
                  ) : (
                    <Tag
                      title={
                        company?.subscriptionStatus
                          ? subscriptionStatusTitle[company?.subscriptionStatus]
                          : 'No subscription'
                      }
                      variant={TagVariant.GRAY}
                    />
                  )}
                </div>
              </div>
              <div className="flex w-full justify-between gap-2">
                <div className="flex w-full flex-col gap-1 rounded-t-[4px] bg-gray-50 px-3 py-2">
                  <span className="text-xs font-[450] text-gray-500">Subscription start</span>
                  <span className="text-sm font-[450] text-gray-700">
                    {company?.latestSubscriptionData?.plan?.created
                      ? format(company?.latestSubscriptionData?.plan?.created * 1000, 'dd/MM/yyyy')
                      : '-'}
                  </span>
                </div>
                <div className="flex w-full flex-col gap-1 rounded-t-[4px] bg-gray-50 px-3 py-2">
                  <span className="text-xs font-[450] text-gray-500">Next Payment</span>
                  <span className="text-sm font-[450] text-gray-700">
                    {company?.nextPayment
                      ? format(Number(company?.nextPayment) * 1000, 'dd/MM/yyyy')
                      : '-'}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex w-full flex-col gap-2">
              <SubscriptionTypeCombobox control={control} />
              <FrequencyCombobox control={control} />
              <FormInput
                control={control}
                disabled={true}
                name={'currency'}
                placeholder="Currency"
                wrapperClassName="w-full"
              />
              <FormInput
                control={control}
                disabled={planType !== SubscriptionType.ENTERPRISE}
                name={'amount'}
                numberOnly
                placeholder="Amount"
                wrapperClassName="w-full"
              />
              <FormInput
                control={control}
                disabled={planType !== SubscriptionType.ENTERPRISE}
                name={'stakeholderLimit'}
                numberOnly
                placeholder="Stakeholder limit"
                wrapperClassName="w-full"
              />
              <FormInput
                control={control}
                name={'docusignEnvelopeLimit'}
                numberOnly
                placeholder="DocuSign envelopes"
                wrapperClassName="w-full"
              />
            </div>
          </div>

          <div className="mt-4 flex h-9 w-full justify-between gap-3 px-6">
            <Button
              className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
              onClick={handleCloseModal}
              styleType="NONE"
              type="button"
            >
              Cancel
            </Button>

            <Button
              className="h-full w-fit px-4 py-[6px] text-sm font-[550] text-white"
              disabled={!isDirty || isLoading || isEnvelopeLimitPending}
              isLoading={isPending || isEnvelopeLimitPending}
              onClick={handleUpdate}
              type="button"
            >
              Update
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
